import { Arrow } from "@components/icons"
import breakpoints from "@constants/breakpoints"
import colors from "@constants/colors"
import { gsap } from "gsap"
import _ from "lodash-es"
import React from "react"
import styled from "styled-components"

const ScrollContainer = styled.div`
  align-items: center;
  justify-content: flex-start;
  flex-direction: "row";
  display: flex;
`

const ScrollTrack = styled.div`
  height: 1px;
  width: 100%;
  background: #000000;
  opacity: 0.1;
`

const ScrollBar = styled.div`
  position: absolute;
  height: 1px;
  background: #000000;
  will-change: transform;
`

export const ScrollButtons = styled.div<{ $showControlsOnMobile: boolean }>`
  display: ${({ $showControlsOnMobile }) =>
    $showControlsOnMobile ? "flex" : "none"};
  flex-direction: "row";
  align-items: center;
  justify-content: flex-start;
  margin-left: 20px;

  @media ${breakpoints.laptop} {
    display: flex;
  }
`

const ScrollButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  margin: 0;
  padding: 0 20px;

  &:focus {
    outline: none;
  }
`

const LeftArrow = styled.div`
  transform: rotate(180deg);
  height: 24px;
  width: 24px;
`

const RightArrow = styled.div`
  height: 24px;
  width: 24px;
`

interface ScrollbarProps {
  className?: string
  containerRef: React.RefObject<HTMLDivElement>
  showControlsOnMobile?: boolean
}

interface ScrollbarState {
  barWidth: number
  showScrollbar: boolean
  translateX: number
}

class Scrollbar extends React.PureComponent<ScrollbarProps, ScrollbarState> {
  barRef = React.createRef<HTMLDivElement>()
  state: ScrollbarState = {
    barWidth: 0,
    showScrollbar: false,
    translateX: 0,
  }
  trackRef = React.createRef<HTMLDivElement>()

  calculateScroll = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    const track = this.trackRef.current
    const bar = this.barRef.current
    if (container && track && bar) {
      // Don't show the scrollbar if there is no overflows
      const showScrollbar = container.scrollWidth > container.clientWidth
      if (!showScrollbar) {
        return this.setState({ showScrollbar })
      }

      // Calcuate the current scroll percentage (0 to 1)
      const scrollPercentage =
        1 -
        (container.scrollWidth - container.scrollLeft - container.clientWidth) /
          (container.scrollWidth - container.clientWidth)

      // Calculate the bar width
      const trackWidth = track.clientWidth
      const barWidth =
        trackWidth * (container.clientWidth / container.scrollWidth)

      // Calculate the translation of the bar
      const widthDiff = trackWidth - barWidth
      const translateX = widthDiff * scrollPercentage

      // Set the styling of the bar
      bar.style.width = `calc(${barWidth}px)`
      bar.style.transform = `translateX(${translateX}px)`
      this.setState({ showScrollbar })
    }
  }

  componentDidMount() {
    const { containerRef } = this.props
    if (containerRef.current) {
      containerRef.current.addEventListener("scroll", this.calculateScroll)
      window.addEventListener("resize", this.calculateScroll)
    }
    this.calculateScroll()
  }

  componentWillUnmount() {
    const { containerRef } = this.props
    if (containerRef.current) {
      containerRef.current.removeEventListener("scroll", this.calculateScroll)
      window.removeEventListener("resize", this.calculateScroll)
    }
  }

  onNext = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft + container.clientWidth,
        0,
        container.scrollWidth - container.clientWidth
      )
      gsap.to(container, {
        duration: 0.4,
        ease: "power4.out",
        scrollLeft: left,
      })
    }
  }

  onPrev = () => {
    const { containerRef } = this.props
    const container = containerRef.current
    if (container) {
      const left = _.clamp(
        container.scrollLeft - container.clientWidth,
        0,
        container.scrollWidth - container.clientWidth
      )
      gsap.to(container, {
        duration: 0.4,
        ease: "power4.out",
        scrollLeft: left,
      })
    }
  }

  render() {
    const { className } = this.props
    return (
      <ScrollContainer className={className}>
        <ScrollTrack ref={this.trackRef} />
        <ScrollBar ref={this.barRef} />
        <ScrollButtons
          $showControlsOnMobile={!!this.props.showControlsOnMobile}
        >
          <ScrollButton aria-label="Scroll Left" onClick={this.onPrev}>
            <LeftArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </LeftArrow>
          </ScrollButton>
          <ScrollButton aria-label="Scroll Right" onClick={this.onNext}>
            <RightArrow>
              <Arrow strokeColor={colors.black} strokeWidth="1.5px" />
            </RightArrow>
          </ScrollButton>
        </ScrollButtons>
      </ScrollContainer>
    )
  }
}

export default Scrollbar
